import {ConsentExperience} from '@github-ui/consent-experience'
import {OctocaptchaContext} from '@github-ui/swp-core/components/forms/OctocaptchaContext'
import {Box, Button, Checkbox, FormControl, Heading, Select, Stack, TextInput, useTheme} from '@primer/react-brand'
import {use, useCallback, useEffect, useRef, useState} from 'react'

import {Octocaptcha} from '../../../../../../../components/Octocaptcha/Octocaptcha'
import {useOctocaptcha} from '../../../../../../../components/Octocaptcha/useOctocaptcha'
import type {GitHubAppWaitlistFormData} from './../../../Index.data'

import Styles from './FormContent.module.css'

export interface FormContentProps {
  data: GitHubAppWaitlistFormData
}

export const FormContent = ({data}: FormContentProps) => {
  const [isConsentValid, setIsConsentValid] = useState<boolean>(true)
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const consentRef = useRef<HTMLDivElement>(null)

  const {colorMode} = useTheme()

  const {hostName, originPage, locale} = use(OctocaptchaContext)
  const {octocaptchaToken, hasOctocaptchaFailedToLoad, onOctocaptchaComplete, onOctocaptchaLoadError} = useOctocaptcha()

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      const form = event.currentTarget

      const errors: Record<string, string> = {}
      let firstInvalid: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null = null

      for (const element of Array.from(form.elements)) {
        if (
          (element instanceof HTMLInputElement ||
            element instanceof HTMLSelectElement ||
            element instanceof HTMLTextAreaElement) &&
          element.name &&
          !element.validity.valid
        ) {
          errors[element.name] = element.validationMessage
          if (!firstInvalid) firstInvalid = element
        }
      }

      const hasFieldErrors = Object.keys(errors).length > 0
      const isCaptchaMissing = !octocaptchaToken && !hasOctocaptchaFailedToLoad

      if (hasFieldErrors || !isConsentValid || isCaptchaMissing) {
        event.preventDefault()
        setFieldErrors(errors)
        if (firstInvalid) {
          firstInvalid.scrollIntoView({behavior: 'smooth', block: 'center'})
          firstInvalid.focus({preventScroll: true})
        }
      }
    },
    [hasOctocaptchaFailedToLoad, isConsentValid, octocaptchaToken],
  )

  const onConsentValidationChange = useCallback((isValid: boolean) => setIsConsentValid(isValid), [])

  useEffect(() => {
    const input = consentRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]')
    if (!input) return

    const syncState = () => setIsConsentChecked(input.checked)

    input.addEventListener('change', syncState)
    syncState()

    return () => {
      input.removeEventListener('change', syncState)
    }
  }, [])

  const renderField = (field: GitHubAppWaitlistFormData['rows'][number]['fields'][number]) => {
    const error = fieldErrors[field.name]
    return (
      <FormControl key={field.name} fullWidth required={field.required} validationStatus={error ? 'error' : undefined}>
        <FormControl.Label>{field.label}</FormControl.Label>
        <TextInput
          name={field.name}
          type={field.type}
          required={field.required}
          placeholder={field.placeholder}
          onInput={() => {
            if (fieldErrors[field.name]) {
              setFieldErrors(prev => {
                const {[field.name]: _, ...rest} = prev
                return rest
              })
            }
          }}
        />
        {error && <FormControl.Validation>{error}</FormControl.Validation>}
      </FormControl>
    )
  }

  return (
    <form
      action={data.eloqua.endpoint}
      method="post"
      noValidate
      className={Styles['FormContent']}
      onSubmit={handleSubmit}
    >
      <Stack direction="vertical" alignItems="flex-start" padding="none">
        {data.rows.map((row, index) => (
          <Stack
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`form-row-${index}`}
            direction="vertical"
            alignItems="flex-start"
            padding="none"
            style={{width: '100%'}}
          >
            {row.heading ? (
              <Heading as="h4" size="5" weight="normal" className={Styles['FormContent__rowHeading']}>
                {row.heading}
              </Heading>
            ) : null}

            {row.fields.length === 2 ? (
              <Stack direction="horizontal" alignItems="flex-start" padding="none" style={{width: '100%'}}>
                {row.fields.map(field => (
                  <Box key={field.name} style={{flex: 5, minWidth: 0}}>
                    {renderField(field)}
                  </Box>
                ))}
              </Stack>
            ) : (
              row.fields.map(field => renderField(field))
            )}
          </Stack>
        ))}

        <FormControl
          id={data.consent.countryFieldSelectorId}
          fullWidth
          required
          validationStatus={fieldErrors[data.country.name] ? 'error' : undefined}
        >
          <FormControl.Label>{data.country.label}</FormControl.Label>
          <Select name={data.country.name} required defaultValue="">
            <Select.Option value="" disabled>
              {data.country.placeholder}
            </Select.Option>
            {data.country.options.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>

          {fieldErrors[data.country.name] && (
            <FormControl.Validation>{String(fieldErrors[data.country.name])}</FormControl.Validation>
          )}
        </FormControl>

        <FormControl fullWidth validationStatus={isConsentValid ? undefined : 'error'}>
          <div ref={consentRef} className={Styles['FormContent__consent']}>
            <ConsentExperience
              {...data.consent}
              countryFieldSelector={`#${data.consent.countryFieldSelectorId} select`}
              onValidationChange={onConsentValidationChange}
              onlySendIfChecked
              implicitOptIn={false}
            />
            <Checkbox aria-hidden="true" tabIndex={-1} checked={isConsentChecked} readOnly inert />
          </div>

          {!isConsentValid && <FormControl.Validation>{data.consent.invalidMessage}</FormControl.Validation>}
        </FormControl>

        <FormControl
          className={Styles['FormContent__octocaptcha']}
          required
          validationStatus={fieldErrors['octocaptcha-token'] ? 'error' : undefined}
        >
          <Octocaptcha
            hostName={hostName}
            originPage={originPage}
            locale={locale}
            onComplete={onOctocaptchaComplete}
            onLoadError={onOctocaptchaLoadError}
            theme={colorMode === 'dark' ? 'dark' : 'light'}
          />
          {fieldErrors['octocaptcha-token'] && (
            <FormControl.Validation>{String(fieldErrors['octocaptcha-token'])}</FormControl.Validation>
          )}
        </FormControl>

        <input type="hidden" name="elqFormName" value={data.eloqua.elqFormName} />
        <input type="hidden" name="elqSiteId" value={data.eloqua.elqSiteId} />
        <input type="hidden" name="source" value={data.eloqua.source} />

        <Button type="submit" variant="accent">
          {data.submitText}
        </Button>
      </Stack>
    </form>
  )
}
